import { requester } from "App";
import P from "components/P";
import Container from "layouts/container/Container";
import FlexChild from "layouts/flex/FlexChild";
import HorizontalFlex from "layouts/flex/HorizontalFlex";
import VerticalFlex from "layouts/flex/VerticalFlex";
import Center from "layouts/wrapper/Center";
import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { formatLocalDate } from "shared/utils/Utils";
import rightIcon from "resources/img/puffu/right.png";
import Dummy from "components/Dummy";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import { devLog } from "utils/logger";

function OrderHistory() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [orderList, setOrderList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [dateFilter, setDateFilter] = useState('all');
    const [isSearchMode, setIsSearchMode] = useState(false);
    const [page, setPage] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [totalElements, setTotalElements] = useState(0);
    const sentinelRef = useRef();
    const loadingPageRef = useRef(null); // 현재 로딩 중인 페이지 추적

    const fetchOrders = useCallback(async (pageNum = 0, resetList = false) => {
        // 중복 호출 방지: 같은 페이지가 이미 로딩 중이면 무시
        if (loadingPageRef.current === pageNum && pageNum > 0) {
            devLog("🚫 중복 호출 방지:", pageNum, "페이지 이미 로딩 중");
            return;
        }

        // 로딩 중인 페이지 설정
        loadingPageRef.current = pageNum;

        if (pageNum === 0) {
            setLoading(true);
        } else {
            setIsLoadingMore(true);
        }

        // 날짜 필터를 startDate/endDate로 변환
        const getDateRange = (filter) => {
            const now = new Date();
            let startDate = null;
            let endDate = null;

            // 로컬 시간대 기준으로 ISO 문자열 생성하는 함수
            const toLocalISOString = (date) => {
                const offset = date.getTimezoneOffset();
                const localDate = new Date(date.getTime() - (offset * 60 * 1000));
                return localDate.toISOString();
            };

            // endDate는 오늘 하루의 끝으로 설정 (23:59:59)
            if (filter !== 'all') {
                const endOfToday = new Date(now);
                endOfToday.setHours(23, 59, 59, 999);
                endDate = toLocalISOString(endOfToday);
            }

            switch (filter) {
                case '1month':
                    // 최근 1개월간의 주문 (1개월 전 ~ 현재)
                    const oneMonthAgo = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
                    oneMonthAgo.setHours(0, 0, 0, 0); // 하루의 시작으로 설정

                    devLog("🗓️ 1개월 필터 날짜 계산:");
                    devLog("📅 현재 날짜:", now.toISOString());
                    devLog("📅 1개월 전 (로컬):", oneMonthAgo.toISOString());
                    devLog("📅 1개월 전 (변환 전):", oneMonthAgo);

                    startDate = toLocalISOString(oneMonthAgo);
                    devLog("📅 최종 startDate:", startDate);
                    break;
                case '3month':
                    // 최근 3개월간의 주문 (3개월 전 ~ 현재)
                    const threeMonthAgo = new Date(now.getFullYear(), now.getMonth() - 3, now.getDate());
                    threeMonthAgo.setHours(0, 0, 0, 0);
                    startDate = toLocalISOString(threeMonthAgo);
                    break;
                case '6month':
                    // 최근 6개월간의 주문 (6개월 전 ~ 현재)
                    const sixMonthAgo = new Date(now.getFullYear(), now.getMonth() - 6, now.getDate());
                    sixMonthAgo.setHours(0, 0, 0, 0);
                    startDate = toLocalISOString(sixMonthAgo);
                    break;
                case 'all':
                default:
                    startDate = null;
                    endDate = null; // 전체 조회 시에는 endDate도 null
                    break;
            }

            return {
                startDate: startDate,
                endDate: endDate
            };
        };

        const { startDate, endDate } = getDateRange(dateFilter);

        const paginationDto = {
            page: pageNum,
            size: 10,
            startDate: startDate,
            endDate: endDate,
            searchTerm: searchTerm || null
        };
        devLog("[OrderHistory] paginationDto", paginationDto);

        requester.findAllPaginatedPurchaseOrder(paginationDto, (result) => {
            if (result.code === 0 && result.data) {
                devLog(result.data);
                devLog("=== 📊 페이징 디버그 정보 ===");
                devLog("🔢 페이지:", pageNum);
                devLog("📦 받아온 주문 수:", result.data.orders.length);
                devLog("📊 전체 주문 수:", result.data.totalElements);
                devLog("🆔 받아온 주문 ID들:", result.data.orders.map(order => order.id));
                devLog("📅 받아온 주문 날짜들:", result.data.orders.map(order => order.createDateTime));

                const { orders, totalElements: total } = result.data;

                if (resetList || pageNum === 0) {
                    devLog("🔄 리스트 초기화 - 기존 배열 길이:", orderList.length);
                    setOrderList(orders);
                    // 첫 페이지일 때는 새로운 orders 길이로 계산
                    setHasMore(orders.length < total);
                    devLog("✅ 새로운 배열 길이:", orders.length);
                } else {
                    devLog("➕ 리스트 추가 - 기존 배열 길이:", orderList.length);
                    devLog("🆔 기존 주문 ID들:", orderList.map(order => order.id));
                    setOrderList(prev => {
                        const newList = [...prev, ...orders];
                        devLog("✅ 합쳐진 배열 길이:", newList.length);
                        devLog("🆔 합쳐진 주문 ID들:", newList.map(order => order.id));

                        // 중복 ID 체크
                        const allIds = newList.map(order => order.id);
                        const uniqueIds = [...new Set(allIds)];
                        if (allIds.length !== uniqueIds.length) {
                            console.warn("⚠️ 중복된 주문 ID 발견!");
                            console.warn("전체 ID 수:", allIds.length, "고유 ID 수:", uniqueIds.length);
                        }

                        // 기존 리스트 + 새로운 orders 길이로 계산
                        setHasMore(newList.length < total);
                        devLog("🔄 hasMore 상태:", newList.length < total, `(${newList.length} < ${total})`);
                        return newList;
                    });
                }

                setTotalElements(total);
                devLog("=== 📊 디버그 정보 끝 ===\n");
            }

            if (pageNum === 0) {
                setLoading(false);
            } else {
                setIsLoadingMore(false);
            }

            // 로딩 완료 후 참조 초기화
            loadingPageRef.current = null;
        });
    }, [dateFilter, searchTerm]);

    // 무한 스크롤
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && hasMore && !isLoadingMore && !loading && loadingPageRef.current === null) {
                    // 중복 호출 방지를 위한 디바운싱 (시간 증가)
                    setTimeout(() => {
                        if (hasMore && !isLoadingMore && !loading && loadingPageRef.current === null) {
                            setPage(prev => {
                                const nextPage = prev + 1;
                                devLog("🔄 무한 스크롤 트리거 - 다음 페이지:", nextPage);
                                fetchOrders(nextPage);
                                return nextPage;
                            });
                        }
                    }, 300); // 100ms → 300ms로 증가
                }
            },
            { threshold: 1.0 }
        );

        if (sentinelRef.current) {
            observer.observe(sentinelRef.current);
        }

        return () => observer.disconnect();
    }, [hasMore, isLoadingMore, loading, fetchOrders]);

    // 초기 로드 (최초 한 번만)
    useEffect(() => {
        setPage(0);
        fetchOrders(0, true);
    }, []); // dateFilter dependency 제거

    // 검색 실행
    const handleSearch = () => {
        if (searchTerm.trim()) {
            setIsSearchMode(true);
            setPage(0);
            fetchOrders(0, true);
        }
    };

    // 검색 초기화
    const handleReset = () => {
        setSearchTerm('');
        setIsSearchMode(false);
        setDateFilter('all');
        setPage(0);
        setHasMore(true);
        setOrderList([]);
        loadingPageRef.current = null;

        // 상태 업데이트 완료 후 전체 데이터 다시 불러오기 (명시적으로 전체 조회)
        setTimeout(() => {
            setLoading(true);

            const paginationDto = {
                page: 0,
                size: 10,
                startDate: null,    // 전체 조회
                endDate: null,      // 전체 조회  
                searchTerm: null    // 검색어 없음
            };

            requester.findAllPaginatedPurchaseOrder(paginationDto, (result) => {
                if (result.code === 0 && result.data) {
                    const { orders, totalElements: total } = result.data;
                    setOrderList(orders);
                    setHasMore(orders.length < total);
                    setTotalElements(total);
                }
                setLoading(false);
            });
        }, 0);
    };

    // 날짜 필터 변경
    const handleDateFilterChange = (filter) => {
        setDateFilter(filter);
        setPage(0);
        setHasMore(true);
        setOrderList([]);
        loadingPageRef.current = null;

        // 상태 업데이트 완료 후 데이터 다시 불러오기 (검색어 + 날짜 필터 조합)
        setTimeout(() => {
            setLoading(true);

            // 날짜 필터를 startDate/endDate로 변환
            const getDateRange = (selectedFilter) => {
                const now = new Date();
                let startDate = null;
                let endDate = null;

                // 로컬 시간대 기준으로 ISO 문자열 생성하는 함수
                const toLocalISOString = (date) => {
                    const offset = date.getTimezoneOffset();
                    const localDate = new Date(date.getTime() - (offset * 60 * 1000));
                    return localDate.toISOString();
                };

                // endDate는 오늘 하루의 끝으로 설정 (23:59:59)
                if (selectedFilter !== 'all') {
                    const endOfToday = new Date(now);
                    endOfToday.setHours(23, 59, 59, 999);
                    endDate = toLocalISOString(endOfToday);
                }

                switch (selectedFilter) {
                    case '1month':
                        const oneMonthAgo = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
                        oneMonthAgo.setHours(0, 0, 0, 0);
                        startDate = toLocalISOString(oneMonthAgo);
                        break;
                    case '3month':
                        const threeMonthAgo = new Date(now.getFullYear(), now.getMonth() - 3, now.getDate());
                        threeMonthAgo.setHours(0, 0, 0, 0);
                        startDate = toLocalISOString(threeMonthAgo);
                        break;
                    case '6month':
                        const sixMonthAgo = new Date(now.getFullYear(), now.getMonth() - 6, now.getDate());
                        sixMonthAgo.setHours(0, 0, 0, 0);
                        startDate = toLocalISOString(sixMonthAgo);
                        break;
                    case 'all':
                    default:
                        startDate = null;
                        endDate = null;
                        break;
                }

                return { startDate, endDate };
            };

            const { startDate, endDate } = getDateRange(filter);

            const paginationDto = {
                page: 0,
                size: 10,
                startDate: startDate,
                endDate: endDate,
                searchTerm: searchTerm || null    // 현재 검색어 유지 (있으면 검색어 + 날짜 필터 조합)
            };

            requester.findAllPaginatedPurchaseOrder(paginationDto, (result) => {
                if (result.code === 0 && result.data) {
                    const { orders, totalElements: total } = result.data;
                    setOrderList(orders);
                    setHasMore(orders.length < total);
                    setTotalElements(total);
                }
                setLoading(false);
            });
        }, 0);
    };

    const handleOrderClick = (order) => {
        navigate(`/ws/${id}/mobile/receipt/detail/${order.id}`, {
            state: { data: order }
        });
    };

    return (
        <div style={{ height: "100%", backgroundColor: "#fff" }}>
            {/* 헤더 공간 */}
            <Dummy height={"60px"} />
            
            {/* 헤더 */}
            <div style={{
                position: 'sticky',
                top: 0,
                backgroundColor: '#fff',
                zIndex: 10,
                borderBottom: '1px solid #f0f0f0'
            }}>
                {/* 검색바 */}
                <div style={{ 
                    padding: 'min(12px, 3vw) min(16px, 4vw)', 
                    width: '100%',
                    boxSizing: 'border-box'
                }}>
                    <HorizontalFlex gap={4}>
                        <FlexChild>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                backgroundColor: '#f5f5f5',
                                borderRadius: '6px',
                                padding: '0 6px',
                                height: 'clamp(36px, 9vw, 40px)',
                                minHeight: '36px'
                            }}>
                                <FontAwesomeIcon
                                    icon={faSearch}
                                    style={{ 
                                        color: '#999', 
                                        marginRight: '4px',
                                        fontSize: 'clamp(11px, 3vw, 13px)'
                                    }}
                                />
                                <input
                                    type="text"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                                    placeholder="상품 검색"
                                    style={{
                                        border: 'none',
                                        backgroundColor: 'transparent',
                                        flex: 1,
                                        fontSize: 'clamp(12px, 3vw, 14px)',
                                        outline: 'none',
                                        minWidth: 0
                                    }}
                                />
                            </div>
                        </FlexChild>
                        <FlexChild width={"max-content"} flexShrink={0}>
                            <button
                                onClick={handleSearch}
                                style={{
                                    padding: 'clamp(6px, 1.5vw, 8px) clamp(8px, 2vw, 12px)',
                                    backgroundColor: 'var(--main-color)',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: '6px',
                                    fontSize: 'clamp(11px, 2.5vw, 13px)',
                                    cursor: 'pointer',
                                    minWidth: 'clamp(40px, 10vw, 50px)',
                                    whiteSpace: 'nowrap',
                                    height: 'clamp(36px, 9vw, 40px)'
                                }}
                            >
                                검색
                            </button>
                        </FlexChild>
                    </HorizontalFlex>
                </div>

                {/* 날짜 필터 */}
                <div style={{ 
                    padding: '0 min(16px, 4vw) min(12px, 3vw) min(16px, 4vw)',
                    boxSizing: 'border-box'
                }}>
                    <HorizontalFlex gap={3}>
                        {[
                            { key: 'all', label: '전체' },
                            { key: '1month', label: '1개월' },
                            { key: '3month', label: '3개월' },
                            { key: '6month', label: '6개월' }
                        ].map(filter => (
                            <FlexChild key={filter.key}>
                                <button
                                    onClick={() => handleDateFilterChange(filter.key)}
                                    style={{
                                        padding: 'clamp(4px, 1vw, 6px) clamp(4px, 1vw, 8px)',
                                        backgroundColor: dateFilter === filter.key ? 'var(--main-color)' : '#fff',
                                        color: dateFilter === filter.key ? '#fff' : '#666',
                                        border: '1px solid #ddd',
                                        borderRadius: '4px',
                                        fontSize: 'clamp(10px, 2.5vw, 12px)',
                                        cursor: 'pointer',
                                        width: '100%',
                                        minHeight: 'clamp(28px, 7vw, 32px)',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis'
                                    }}
                                >
                                    {filter.label}
                                </button>
                            </FlexChild>
                        ))}
                        {isSearchMode && (
                            <FlexChild width={"max-content"} flexShrink={0}>
                                <button
                                    onClick={handleReset}
                                    style={{
                                        padding: 'clamp(4px, 1vw, 6px) clamp(6px, 1.5vw, 8px)',
                                        backgroundColor: 'transparent',
                                        border: '1px solid #ff6b6b',
                                        borderRadius: '4px',
                                        fontSize: 'clamp(9px, 2vw, 11px)',
                                        color: '#ff6b6b',
                                        cursor: 'pointer',
                                        minWidth: 'clamp(40px, 10vw, 50px)',
                                        minHeight: 'clamp(28px, 7vw, 32px)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '1px',
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    <span style={{ fontSize: 'clamp(8px, 2vw, 10px)' }}>✕</span>
                                    <span>초기화</span>
                                </button>
                            </FlexChild>
                        )}
                    </HorizontalFlex>
                </div>

                {/* 검색 결과 표시 */}
                {isSearchMode && (
                    <div style={{ padding: "8px 20px", backgroundColor: "#f8f9fa" }}>
                        <P size={14} color="#666">
                            '{searchTerm}' 검색 결과 {totalElements}건
                        </P>
                    </div>
                )}
            </div>

            {/* 주문 목록 */}
            <VerticalFlex height={"max-content"} padding={"0 20px"}>
                {loading ? (
                    <Center height={"200px"}>
                        <P color={"#666"}>주문 내역을 불러오는 중...</P>
                    </Center>
                ) : orderList.length > 0 ? (
                    <>
                        {orderList.map((order, index) => {
                            const totalProducts = order.orderProducts.length;

                            return (
                                <FlexChild key={order.id} height={"max-content"}>
                                    {/* 날짜 헤더 */}
                                    {(index === 0 || formatLocalDate(order.createDateTime).split(' ')[0] !== formatLocalDate(orderList[index - 1].createDateTime).split(' ')[0]) && (
                                        <div style={{
                                            padding: '16px 0 8px',
                                            backgroundColor: '#fff'
                                        }}>
                                            <P size={16} weight={600}>
                                                {formatLocalDate(order.createDateTime).split(' ')[0]}
                                            </P>
                                        </div>
                                    )}

                                    {/* 주문 카드 */}
                                    <div
                                        style={{
                                            backgroundColor: "#FFFFFF",
                                            padding: "16px 20px",
                                            borderBottom: "1px solid #f5f5f5",
                                            border: "1px solid #e0e0e0",
                                            borderRadius: "8px",
                                            margin: "8px 20px"
                                        }}
                                    >
                                        <VerticalFlex gap={12}>
                                            {/* 주문 시간 및 상태 */}
                                            <HorizontalFlex justifyContent="space-between" alignItems="center">
                                                <P size={14} color="#666">
                                                    {formatLocalDate(order.createDateTime).split(' ')[1]}
                                                </P>
                                                <HorizontalFlex alignItems="center" gap={8}>
                                                    <FlexChild width={"max-content"} justifyContent={"flex-start"}>
                                                        <div style={{
                                                            
                                                            padding: "4px 8px",
                                                            backgroundColor: order.deliveryStatus === "WaitingForPayment" ? "#fff3cd" : "#d4edda",
                                                            borderRadius: "4px",
                                                            border: `1px solid ${order.deliveryStatus === "WaitingForPayment" ? "#ffeaa7" : "#c3e6cb"}`
                                                        }}>
                                                            <P size={12} color={order.deliveryStatus === "WaitingForPayment" ? "#856404" : "#155724"} weight={500}>
                                                                {order.deliveryStatus === "WaitingForPayment" ? "결제대기" : "구매완료"}
                                                            </P>
                                                        </div>
                                                    </FlexChild>
                                                    <FlexChild justifyContent={"flex-end"}>
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleOrderClick(order);
                                                            }}
                                                            style={{
                                                                padding: "6px 12px",
                                                                backgroundColor: "var(--main-color)",
                                                                color: "#fff",
                                                                border: "none",
                                                                borderRadius: "4px",
                                                                fontSize: "12px",
                                                                cursor: "pointer",
                                                                fontWeight: "500"
                                                            }}
                                                        >
                                                            주문서 상세보기
                                                        </button>
                                                    </FlexChild>
                                                </HorizontalFlex>
                                            </HorizontalFlex>

                                            {/* 상품 목록 */}
                                            <VerticalFlex gap={10}>
                                                {order.orderProducts.map((product, productIndex) => (
                                                    <div key={product.id || productIndex}>
                                                        <HorizontalFlex gap={12} alignItems="flex-start">
                                                            <FlexChild width={"60px"} flexShrink={0}>
                                                                <div style={{
                                                                    width: "60px",
                                                                    height: "60px",
                                                                    borderRadius: "8px",
                                                                    overflow: "hidden",
                                                                    backgroundColor: "#f5f5f5",
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    justifyContent: "center"
                                                                }}>
                                                                    {product.productImg ? (
                                                                        <img
                                                                            src={product.productImg}
                                                                            alt={product.productName}
                                                                            style={{
                                                                                width: "100%",
                                                                                height: "100%",
                                                                                objectFit: "cover"
                                                                            }}
                                                                        />
                                                                    ) : (
                                                                        <P size={12} color="#999">이미지</P>
                                                                    )}
                                                                </div>
                                                            </FlexChild>
                                                            <FlexChild flex={1}>
                                                                <VerticalFlex flexStart gap={6}>
                                                                    <P
                                                                        color={"#333"}
                                                                        size={15}
                                                                        weight={500}
                                                                        style={{
                                                                            lineHeight: '1.3',
                                                                            wordBreak: 'keep-all',
                                                                            overflowWrap: 'break-word'
                                                                        }}
                                                                    >
                                                                        {product.productName}
                                                                    </P>
                                                                    <HorizontalFlex justifyContent="space-between" alignItems="center">
                                                                        <P color={"#666"} size={13}>
                                                                            {product.price.toLocaleString()}원 × {product.quantity}개
                                                                        </P>
                                                                        <P color={"#333"} size={15} weight={600}>
                                                                            {(product.price * product.quantity).toLocaleString()}원
                                                                        </P>
                                                                    </HorizontalFlex>
                                                                </VerticalFlex>
                                                            </FlexChild>
                                                        </HorizontalFlex>
                                                        {/* {productIndex < order.orderProducts.length - 1 && order.orderProducts.length > 1 && (
                                                            <div style={{ height: '1px', backgroundColor: '#f0f0f0', margin: '10px 0' }} />
                                                        )} */}
                                                    </div>
                                                ))}
                                            </VerticalFlex>

                                            {/* 총 금액 및 정보 */}
                                            <div style={{
                                                padding: "12px 0",
                                                borderTop: "1px solid #e0e0e0",
                                                marginTop: "8px"
                                            }}>
                                                <VerticalFlex gap={8}>
                                                    {/* 총 상품 개수와 포인트 */}
                                                    <HorizontalFlex justifyContent="space-between" alignItems="center">
                                                        <P color={"#666"} size={13}>
                                                            총 {totalProducts}개 상품
                                                        </P>
                                                        {order.usedPoints > 0 && (
                                                            <P color={"#ff6600"} size={13}>
                                                                포인트 사용 {order.usedPoints.toLocaleString()}원
                                                            </P>
                                                        )}
                                                    </HorizontalFlex>

                                                    {/* 총 금액 */}
                                                    <HorizontalFlex justifyContent="space-between" alignItems="center">
                                                        <P color={"#333"} size={16} weight={600}>
                                                            총 결제금액
                                                        </P>
                                                        <P color={"#333"} size={18} weight={700}>
                                                            {order.amount.toLocaleString()}원
                                                        </P>
                                                    </HorizontalFlex>
                                                </VerticalFlex>
                                            </div>

                                            {/* 주문 요약 정보 */}
                                            <div style={{
                                                padding: "8px 12px",
                                                backgroundColor: "#f8f9fa",
                                                borderRadius: "4px"
                                            }}>
                                                <HorizontalFlex justifyContent="space-between" alignItems="center">
                                                    <P size={12} color="#666">주문번호</P>
                                                    <P 
                                                        size={12} 
                                                        color="#333" 
                                                        weight={500}
                                                        style={{
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap',
                                                            maxWidth: '200px'
                                                        }}
                                                    >
                                                        {order.id}
                                                    </P>
                                                </HorizontalFlex>
                                            </div>
                                        </VerticalFlex>
                                    </div>
                                </FlexChild>
                            );
                        })}

                        {/* 무한 스크롤 센티넬 */}
                        <div ref={sentinelRef} style={{ height: '20px' }}>
                            {isLoadingMore && (
                                <Center>
                                    <P color={"#666"} size={14}>더 불러오는 중...</P>
                                </Center>
                            )}
                        </div>
                    </>
                ) : (
                    <Center height={"calc(100vh - 300px)"} padding={"10px 0 0 0"}>
                        <VerticalFlex gap={16}>
                            <P color={"#666"} size={16}>주문 내역이 없습니다</P>
                            <P color={"#999"} size={14}>첫 주문을 시작해보세요!</P>
                        </VerticalFlex>
                    </Center>
                )}
            </VerticalFlex>

            <Dummy height={"80px"} />
        </div>
    );
}

export default OrderHistory; 